import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import Logger from '@/common/utils/Logger';
import { getTicketById } from '@/service/ticketService';
import { getFormById } from '@/service/formManagementService';

/**
 * Shared helpers for the "K-Board システムプロンプト" field.
 *
 * The field works like the existing System Prompt mechanism: the user pastes a
 * knowledge share link, we validate that the linked ticket is an active knowledge
 * created with the [K-Boardプロンプト] system form, and then display the prompt name
 * as a Chip. Only one prompt can be selected per screen.
 *
 * These helpers are screen-agnostic so they can be reused by Project settings,
 * Organization settings and Product version create/edit.
 */

/**
 * Resolve a K-Board System Prompt share link into its ticket detail.
 * @returns {Promise<{ isValid: boolean, ticketDetail: object }>}
 */
export const resolveKBoardPromptTicket = async (linkValue) => {
    if (Utils.isEmptyString(linkValue) || !Utils.isValidLinkPromptInput(linkValue)) {
        return { isValid: false, ticketDetail: {} };
    }

    try {
        const ticketId = await Utils.getTicketIdByShareLink(linkValue);
        const ticketDetail = await getTicketById(null, ticketId);

        // ticketDetail.formId is a JSON like { formId: "<actual form id>" },
        // so we must fetch the real form to know its definition/variant.
        const ticketForm = Utils.convertJSONDataToObject(ticketDetail?.formId);
        const formDetail = await getFormById(null, ticketForm?.formId);

        const isValid =
            !Utils.isEmptyObject(ticketDetail) &&
            Utils.isEmptyString(ticketDetail?.deletedAt) &&
            ticketDetail?.promptFlag === Constant.ACTIVE_FLAG.ACTIVE &&
            ticketDetail?.public !== Constant.TICKET_PUBLIC.DRAFT &&
            Utils.isSystemFormKBoardPrompt(formDetail);

        return { isValid, ticketDetail: !Utils.isEmptyObject(ticketDetail) ? ticketDetail : {} };
    } catch (err) {
        Logger.error('resolveKBoardPromptTicket error :>>', err);
        return { isValid: false, ticketDetail: {} };
    }
};

/**
 * onBlur handler for the share-link input. Validates the link and, when valid,
 * stores the link + ticket detail so the UI can render a chip.
 */
export const handleOnBlurKBoardSystemPrompt = async (
    e,
    setKBoardSystemPromptLink,
    setKBoardPromptDetail,
    setErrorKBoardSystemPrompt,
    setIsLoading,
    setIsEdited
) => {
    const linkValue = e.target.value;

    if (Utils.isEmptyString(linkValue)) {
        setKBoardSystemPromptLink('');
        setKBoardPromptDetail({});
        setErrorKBoardSystemPrompt('');
        if (setIsEdited) setIsEdited(true);
        return;
    }

    if (!Utils.isValidLinkPromptInput(linkValue)) {
        setErrorKBoardSystemPrompt(Constant.AI_PROMPT_VALID_CODE.MSG_CM_INVALID_LINK);
        return;
    }

    if (setIsLoading) setIsLoading(true);
    try {
        const { isValid, ticketDetail } = await resolveKBoardPromptTicket(linkValue);

        if (!isValid) {
            setErrorKBoardSystemPrompt(Constant.AI_PROMPT_VALID_CODE.MSG_CM_INVALID_LINK);
            return;
        }

        setKBoardSystemPromptLink(linkValue);
        setKBoardPromptDetail(ticketDetail);
        setErrorKBoardSystemPrompt('');
        if (setIsEdited) setIsEdited(true);
    } catch (err) {
        Logger.error('handleOnBlurKBoardSystemPrompt error :>>', err);
        setErrorKBoardSystemPrompt(Constant.AI_PROMPT_VALID_CODE.MSG_CM_INVALID_LINK);
    } finally {
        if (setIsLoading) setIsLoading(false);
    }
};

/**
 * Clear the currently selected K-Board System Prompt.
 */
export const handleRemoveKBoardSystemPrompt = (
    setKBoardSystemPromptLink,
    setKBoardPromptDetail,
    setErrorKBoardSystemPrompt,
    setIsEdited
) => {
    setKBoardSystemPromptLink('');
    setKBoardPromptDetail({});
    setErrorKBoardSystemPrompt('');
    if (setIsEdited) setIsEdited(true);
};
